import {
  ActivityIcon,
  CalendarClockIcon,
  ChartColumnIcon,
  CompassIcon,
  EllipsisIcon,
  FolderIcon,
  LibraryIcon,
  ListOrderedIcon,
  MonitorPlayIcon,
  PlugZapIcon,
  ScrollTextIcon,
  SearchIcon,
  SettingsIcon,
  ShuffleIcon,
  SlidersHorizontalIcon,
  TvIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Suspense, lazy, useEffect, useState } from "react";
import { NavLink, Route, Routes, useLocation, useNavigate } from "react-router";
import { AuthCorner, describeOidcError } from "./components/auth-corner";
import { MediaTracker } from "./components/media-tracker";
import { NotificationBell } from "./components/notification-bell";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { PageLoading } from "./components/ui/loading";
import { Toaster } from "./components/ui/toast";
import { TooltipProvider } from "./components/ui/tooltip";
import { reportCapabilitiesOnce } from "./lib/capabilities";
import { readFlag, writeFlag } from "./lib/device-pref";
import { useSession } from "./lib/use-session";
import { BrowseScreen } from "./screens/browse";
import { HomeScreen } from "./screens/home";
import { InviteScreen } from "./screens/invite";
import { LibraryScreen } from "./screens/library";
import { PlayerScreen } from "./screens/player";
import { RoomScreen } from "./screens/room";
import { SearchScreen } from "./screens/search";
import { TitleScreen } from "./screens/title";
import { useFocusMemory } from "./tv/use-focus-memory";
import { useGamepad } from "./tv/use-gamepad";
import { useSpatialNavigation } from "./tv/use-spatial-navigation";

/**
 * Ekrany doładowywane osobno.
 *
 * Zmierzone przed zmianą: jeden plik 716 KiB (218 KiB po gzipie) zawierał WSZYSTKO,
 * więc widz wchodzący obejrzeć odcinek ściągał też panel administratora, dziennik
 * decyzji, przegląd mapowań, import listy z MyAnimeList i przeglądarkę plików.
 *
 * Podział idzie po jednym kryterium: **czy to jest na drodze od wejścia do
 * odtwarzania**. Start, szukanie, przeglądanie, strona tytułu, odtwarzacz, pokój
 * i wejście z zaproszenia zostają w pierwszym pliku, bo doładowanie w tej ścieżce
 * znaczyłoby pustą klatkę w chwili, gdy ktoś właśnie kliknął „Odtwarzaj" — a na
 * telewizorze sterowanym pilotem wygląda to na zawieszenie, nie na wczytywanie.
 * Pokój zostaje z tego samego powodu w mocniejszej wersji: do pokoju wchodzi się
 * z rozesłanego odnośnika, czyli przy zimnym wczytaniu, więc osobny plik znaczyłby
 * dwa żądania po kolei zamiast jednego. Wszystko inne wchodzi po kliknięciu
 * w nawigacji, gdzie plik doładowuje się równolegle z żądaniem do API.
 *
 * Zmierzone po zmianie: **634 KiB (196 KiB po gzipie)** w pierwszym pliku i jedenaście
 * osobnych, od 3 do 13 KiB. To 22 KiB gzipa mniej, czyli około dziesięciu procent —
 * nie przełom, bo resztę stanowią React, Radix i klient gniazd, których nie da się
 * odłożyć: gniazdo jest potrzebne w pokoju, a Radix w pierwszym widoku.
 */
const AuditScreen = lazy(async () => ({ default: (await import("./screens/audit")).AuditScreen }));
const DiagnosticsScreen = lazy(async () => ({
  default: (await import("./screens/diagnostics")).DiagnosticsScreen,
}));
const FilesScreen = lazy(async () => ({ default: (await import("./screens/files")).FilesScreen }));
const QueueScreen = lazy(async () => ({ default: (await import("./screens/queue")).QueueScreen }));
const ImportMalScreen = lazy(async () => ({
  default: (await import("./screens/import-mal")).ImportMalScreen,
}));
const MappingsScreen = lazy(async () => ({
  default: (await import("./screens/mappings")).MappingsScreen,
}));
const MySettingsScreen = lazy(async () => ({
  default: (await import("./screens/my-settings")).MySettingsScreen,
}));
const ScheduleScreen = lazy(async () => ({
  default: (await import("./screens/schedule")).ScheduleScreen,
}));
const SettingsScreen = lazy(async () => ({
  default: (await import("./screens/settings")).SettingsScreen,
}));
const SourcesScreen = lazy(async () => ({
  default: (await import("./screens/sources")).SourcesScreen,
}));
const StatsScreen = lazy(async () => ({ default: (await import("./screens/stats")).StatsScreen }));
const UploadScreen = lazy(async () => ({
  default: (await import("./screens/upload")).UploadScreen,
}));

/**
 * Szkielet aplikacji.
 *
 * **Pełna szerokość okna, nie wyśrodkowana kolumna.** Wcześniej treść siedziała
 * w kontenerze o stałej szerokości, przez co całość czytała się jak zwykła
 * strona, a nie jak aplikacja do oglądania: na szerokim ekranie zostawały puste
 * pola po bokach, a pasy kafli urywały się w połowie dostępnego miejsca.
 *
 * Nawigacja jest w dwóch postaciach, bo obsługuje dwa różne urządzenia, a nie
 * jedno w dwóch rozmiarach:
 *
 *  - **Szyna z boku** tam, gdzie jest kursor albo pilot. Pionowa, zawsze widoczna,
 *    ikona plus podpis — nie rozwija się na najechanie, bo rozwijanie przesuwałoby
 *    treść pod kursorem.
 *  - **Pasek dolny** na dotyku. Kciuk sięga dołu ekranu, nie góry; górny pasek
 *    nawigacyjny na telefonie wymaga przełożenia telefonu w dłoni.
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV: ReadonlyArray<NavItem> = [
  { to: "/", label: "Start", icon: MonitorPlayIcon },
  { to: "/szukaj", label: "Szukaj", icon: SearchIcon },
  { to: "/przegladaj", label: "Przeglądaj", icon: CompassIcon },
  { to: "/biblioteka", label: "Biblioteka", icon: LibraryIcon },
  { to: "/harmonogram", label: "Emisje", icon: CalendarClockIcon },
  { to: "/pliki", label: "Pliki", icon: FolderIcon },
  { to: "/kolejka", label: "Kolejka", icon: ListOrderedIcon },
  { to: "/statystyki", label: "Statystyki", icon: ChartColumnIcon },
  { to: "/zrodla", label: "Źródła", icon: PlugZapIcon },
];

/**
 * Narzędzia administratora — w szynie, pod kreską, tylko dla administratora.
 *
 * Do tej pory `/mapowania` istniało wyłącznie jako adres: ekran był zbudowany
 * i **niedostępny z interfejsu**, czyli funkcjonalnie nieobecny dla każdego, kto
 * nie czytał kodu. Ten sam błąd popełniliśmy wcześniej ze ściągawką skrótów.
 *
 * W pasku dolnym ich nie ma świadomie: to praca przy klawiaturze, a nie kciukiem
 * w tramwaju.
 */
const ADMIN_NAV: ReadonlyArray<NavItem> = [
  { to: "/mapowania", label: "Mapowania", icon: ShuffleIcon },
  { to: "/diagnostyka", label: "Diagnostyka", icon: ActivityIcon },
  { to: "/instancja", label: "Instancja", icon: SlidersHorizontalIcon },
  { to: "/dziennik", label: "Dziennik", icon: ScrollTextIcon },
];

/**
 * Pozycje stojące w pasku dolnym; reszta `NAV` jest pod „Więcej".
 *
 * Dziewięć pozycji z podpisami potrzebuje ponad 500 pikseli, a telefon ma 360–430:
 * pasek wychodził za ekran i ostatnie pozycje (Statystyki, Źródła) były nieosiągalne
 * bez przewijania czegoś, co nie wygląda na przewijalne. Zostaje to, po co sięga się
 * kciukiem co wieczór — reszta to rzeczy, do których się zagląda.
 */
const BOTTOM_PRIMARY: ReadonlySet<string> = new Set(["/", "/szukaj", "/przegladaj", "/biblioteka"]);

/** Tryb TV należy do urządzenia — telewizor go chce, laptop obok nie. */
const TV_PREF = "tvMode";

export function AppShell() {
  const { session } = useSession();
  /**
   * Tryb TV przeżywa odświeżenie strony.
   *
   * Bez tego przełącznik był jednorazowy: telewizor wracał do skali biurkowej po
   * każdym wdrożeniu, po każdym odświeżeniu i po **przełączeniu profilu**, bo ono
   * przeładowuje kartę. Włączanie go za każdym razem pilotem czyni z niego ozdobę.
   */
  const [tv, setTv] = useState(() => readFlag(TV_PREF, false));

  /**
   * Pilot i pad działają ZAWSZE, nie tylko w trybie TV.
   *
   * Ktoś podłącza pada do laptopa i oczekuje, że zadziała; ktoś inny ma telewizor,
   * ale nie zna przełącznika. Wiązanie sterowania z trybem wyświetlania znaczyłoby,
   * że pilot nie działa, dopóki nie znajdzie się ustawienia — pilotem.
   */
  useSpatialNavigation();
  useGamepad();
  /**
   * Powrót na ekran wraca na ostatni zaznaczony kafel.
   *
   * Wywołanie stoi tu, nie w ekranach: pamięć jest wspólna dla całej aplikacji, a
   * odnajdywanie idzie po żywym DOM-ie, więc obejmuje też ekrany, które jeszcze nie
   * istnieją. Działa wyłącznie przy strzałkach — patrz `focus-memory.ts`.
   */
  useFocusMemory();

  /**
   * Raport zdolności dekodowania — raz na urządzenie, po zalogowaniu.
   *
   * Tutaj, a nie w odtwarzaczu: decyzja o przekodowaniu zapada przy PRZETWARZANIU
   * materiału, czyli zanim ktokolwiek otworzy odtwarzacz. Raport wysłany dopiero
   * przy pierwszym odcinku przyszedłby po tej decyzji, czyli za późno.
   */
  useEffect(() => {
    if (session?.authenticated === true) reportCapabilitiesOnce();
  }, [session?.authenticated]);
  const navigate = useNavigate();
  const location = useLocation();

  // Callback OIDC przekierowuje na `/?oidcError=…`, bo to nawigacja
  // przeglądarki — użytkownik musi wylądować na stronie, nie na kopercie JSON.
  const oidcError = new URLSearchParams(location.search).get("oidcError");

  useEffect(() => {
    document.documentElement.dataset.tv = tv ? "true" : "false";
    writeFlag(TV_PREF, tv);
  }, [tv]);

  // Ukośnik jako skrót do wyszukiwania — działa wszędzie poza polem tekstowym,
  // żeby nie przechwytywać znaku podczas pisania.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      // Nawigacja routera, nie window.location — przeładowanie strony zgubiłoby
      // stan aplikacji i zafundowało pełne pobranie zasobów przy każdym skrócie.
      navigate("/szukaj");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <TooltipProvider delayDuration={400}>
      {/* Poświata w tle: statyczna, jeden gradient na całą aplikację. Daje
          głębię bez kosztu — animowana zjadałaby budżet klatki obok wideo. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(60rem_40rem_at_15%_-10%,var(--color-primary)/12%,transparent),radial-gradient(50rem_30rem_at_90%_0%,var(--color-accent)/10%,transparent)]"
      />

      <div className="flex min-h-svh w-full">
        <SideRail admin={session?.user?.isAdmin === true} />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="page-gutter sticky top-0 z-40 flex items-center gap-3 border-b border-border/60 bg-background/80 py-2 backdrop-blur-xl">
            {/* Znak firmowy tylko przy pasku dolnym — przy szynie z boku byłby
                drugim takim samym elementem w jednym widoku. */}
            <NavLink
              to="/"
              className="focus-ring-own flex items-center gap-2 rounded-md px-1 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50 focus-visible:outline-none md:hidden"
            >
              <span className="flex size-7 items-center justify-center rounded-md bg-gradient-to-br from-primary to-accent">
                <MonitorPlayIcon className="size-4 text-primary-foreground" />
              </span>
              <span className="text-base font-semibold tracking-tight">Anisync</span>
            </NavLink>

            <div className="ml-auto flex items-center gap-2">
              {/* Dzwonek tylko dla zalogowanych: powiadomienia są przypisane do
                  konta, a gość nie ma trwałej tożsamości, do której dałoby się je
                  przypiąć. `AuthCorner` sam wie, czy sesja jest — dzwonek pyta
                  o to samo źródło. */}
              <NotificationBellSlot />
              {/* Ustawienia widza w nagłówku, nie w szynie: to miejsce, w którym
                  ludzie ich szukają, a szyna ma już osiem pozycji i rośnie o każdą
                  nową sekcję. Tylko dla zalogowanych — bez konta nie ma czego
                  zapisać. */}
              {session?.authenticated === true && (
                <Button variant="ghost" size="icon" asChild title="Moje ustawienia">
                  <NavLink to="/ustawienia" aria-label="Moje ustawienia">
                    <SettingsIcon />
                  </NavLink>
                </Button>
              )}
              <AuthCorner />

              <Button
                variant={tv ? "secondary" : "outline"}
                size="sm"
                onClick={() => setTv((v) => !v)}
                aria-pressed={tv}
                title="Powiększa cały interfejs do czytania z kilku metrów"
              >
                <TvIcon />
                <span className="hidden sm:inline">{tv ? "Wyłącz TV" : "Tryb TV"}</span>
              </Button>
            </div>
          </header>

          {oidcError !== null && (
            <div className="page-gutter pt-4">
              <Alert variant="destructive">
                <AlertDescription>
                  Logowanie nie powiodło się: {describeOidcError(oidcError)}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/*
            Odstęp od dołu robi miejsce paskowi dolnemu RAZEM z obszarem
            bezpiecznym telefonu — bez tego ostatni wiersz listy siedzi pod
            paskiem systemowym i nie da się go dotknąć.
          */}
          <main className="page-gutter min-w-0 flex-1 pt-5 pb-[calc(4.75rem+env(safe-area-inset-bottom))] md:pb-10">
            {/* Jedna granica na wszystkie trasy, nie jedna na ekran: fallback jest
                ten sam, a zagnieżdżanie po ekranie znaczyłoby jedenaście miejsc do
                utrzymania w zgodzie. */}
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<HomeScreen />} />
                <Route path="/szukaj" element={<SearchScreen />} />
                <Route path="/przegladaj" element={<BrowseScreen />} />
                <Route path="/statystyki" element={<StatsScreen />} />
                <Route path="/biblioteka" element={<LibraryScreen />} />
                <Route path="/harmonogram" element={<ScheduleScreen />} />
                <Route path="/tytul/:titleId" element={<TitleScreen />} />
                <Route path="/odtwarzacz/:mediaFileId" element={<PlayerScreen />} />
                <Route path="/pokoj/:slug" element={<RoomScreen />} />
                <Route path="/z/:code" element={<InviteScreen />} />
                <Route path="/pliki" element={<FilesScreen />} />
                <Route path="/kolejka" element={<QueueScreen />} />
                <Route path="/wyslij" element={<UploadScreen />} />
                <Route path="/import" element={<ImportMalScreen />} />
                <Route path="/zrodla" element={<SourcesScreen />} />
                <Route path="/mapowania" element={<MappingsScreen />} />
                <Route path="/diagnostyka" element={<DiagnosticsScreen />} />
                {/* Ustawienia instancji zmieniają zachowanie dla wszystkich, więc
                  siedzą pod osobnym adresem i w sekcji administratora. `/ustawienia`
                  należy do widza. */}
                <Route path="/instancja" element={<SettingsScreen />} />
                <Route path="/ustawienia" element={<MySettingsScreen />} />
                <Route path="/dziennik" element={<AuditScreen />} />
                <Route
                  path="*"
                  element={
                    <p className="text-muted-foreground">
                      Nie ma takiej strony. Wróć na{" "}
                      <NavLink to="/" className="text-accent underline underline-offset-4">
                        start
                      </NavLink>
                      .
                    </p>
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>

      <BottomBar />
      {/*
        Powiadomienia i śledzenie uruchomionych odcinków — w powłoce, nie na ekranie
        tytułu: odcinek uruchomiony z listy pobiera się dalej, gdy widz przejdzie
        gdzie indziej, i to tam ma się dowiedzieć, że jest gotowy.
      */}
      <Toaster />
      <MediaTracker />
    </TooltipProvider>
  );
}

/**
 * Dzwonek pokazujemy wyłącznie po zalogowaniu.
 *
 * Osobny komponencik, żeby `useSession` nie wymuszał przerysowania całego
 * szkieletu przy każdej zmianie sesji — a przy okazji miejsce na warunek jest
 * jedno, a nie rozsypane po nagłówku.
 */
function NotificationBellSlot() {
  const { session } = useSession();
  if (session?.authenticated !== true) return null;
  return <NotificationBell />;
}

/** Wspólne klasy pozycji nawigacji — jedne dla szyny, jedne dla paska. */
function navClasses(isActive: boolean, vertical: boolean): string {
  return [
    "focus-ring-own flex items-center justify-center gap-1 rounded-lg outline-none",
    "transition-colors duration-150 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50",
    vertical ? "flex-col px-1 py-2 text-[0.625rem]" : "flex-col px-3 py-1.5 text-[0.6875rem]",
    isActive
      ? "bg-secondary text-foreground"
      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
  ].join(" ");
}

function SideRail({ admin }: { admin: boolean }) {
  return (
    <nav
      aria-label="Nawigacja główna"
      className="sticky top-0 hidden h-svh w-[5.25rem] shrink-0 flex-col gap-1 border-r border-border/60 bg-background/50 px-2 py-3 backdrop-blur-xl md:flex"
    >
      <NavLink
        to="/"
        end
        aria-label="Anisync — strona główna"
        className="focus-ring-own mb-3 flex justify-center rounded-md py-1 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-accent">
          <MonitorPlayIcon className="size-5 text-primary-foreground" />
        </span>
      </NavLink>

      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          className={({ isActive }) => navClasses(isActive, true)}
        >
          <item.icon className="size-5" />
          {item.label}
        </NavLink>
      ))}

      {admin && (
        <>
          {/* Kreska, nie nagłówek: podpis „Administracja" zajmowałby w szynie tyle
              samo miejsca co cała pozycja, a rozdziela dokładnie to samo. */}
          <div className="mx-2 my-2 border-t border-border/60" />
          {ADMIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => navClasses(isActive, true)}
            >
              <item.icon className="size-5" />
              {item.label}
            </NavLink>
          ))}
        </>
      )}
    </nav>
  );
}

/**
 * Pasek dolny — dotyk.
 *
 * Stały, bo nawigacja, która odjeżdża razem z treścią, wymusza przewinięcie na
 * samą górę albo na sam dół, żeby przejść gdziekolwiek. Podkładka obszaru
 * bezpiecznego jest w środku paska, nie pod nim, więc tło sięga krawędzi ekranu
 * także na telefonach z zaokrąglonym dołem.
 */
function BottomBar() {
  const { pathname } = useLocation();
  const primary = NAV.filter((item) => BOTTOM_PRIMARY.has(item.to));
  const more = NAV.filter((item) => !BOTTOM_PRIMARY.has(item.to));
  // „Więcej" świeci jak aktywna pozycja, gdy jesteśmy na którymś z jego ekranów —
  // inaczej pasek nie mówiłby, gdzie się jest.
  const moreActive = more.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );

  return (
    <nav
      aria-label="Nawigacja główna"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden"
    >
      <div className="flex items-stretch justify-around gap-1 px-1 py-1">
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) => `${navClasses(isActive, false)} flex-1`}
          >
            <item.icon className="size-5" />
            {item.label}
          </NavLink>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger className={`${navClasses(moreActive, false)} flex-1`}>
            <EllipsisIcon className="size-5" />
            Więcej
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end">
            {/* Podświetlenie bieżącej pozycji przez `aria-current`, który NavLink
                ustawia sam: klasa-funkcja NavLinka nie przechodzi przez `asChild`. */}
            {more.map((item) => (
              <DropdownMenuItem
                key={item.to}
                asChild
                className="aria-[current=page]:bg-secondary aria-[current=page]:font-medium"
              >
                <NavLink to={item.to}>
                  <item.icon />
                  {item.label}
                </NavLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
